import React, { forwardRef } from 'react';
import classNames from 'classnames';

import { NamedLink } from '../../components';

import css from './DashboardMenuItem.module.css';

const CheckGlyph = () => (
  <svg
    className={css.check}
    width="14"
    height="11"
    viewBox="0 0 14 11"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M1 5.5L5 9.5L13 1.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

/**
 * A single row inside a DashboardMenu.
 *
 * Renders a real <button> or a <NamedLink> so focus and keyboard activation
 * come from the platform rather than from a click handler on a bare <li>.
 *
 * Selected rows are marked twice, with an inset rule and a check glyph, so the
 * state does not depend on colour alone. Disabled rows keep aria-disabled and
 * stay in the accessibility tree instead of being removed.
 *
 * @component
 * @param {Object} props
 * @param {ReactNode} props.children - the row label
 * @param {Function?} props.onClick - renders a <button>; mutually exclusive with `to`
 * @param {string|Object?} props.to - NamedLink `name`, or {name, params}; renders a link
 * @param {boolean?} props.selected - current item; adds aria-current="page"
 * @param {boolean?} props.disabled - adds aria-disabled and blocks activation
 * @param {boolean?} props.destructive - destructive styling
 * @param {number?} props.count - trailing count pill
 * @param {string?} props.meta - trailing meta text
 * @param {string?} props.hint - second line under the label
 * @returns {JSX.Element} menu row
 */
const DashboardMenuItem = forwardRef((props, ref) => {
  const {
    children,
    className,
    rootClassName,
    onClick,
    to,
    selected,
    disabled,
    destructive,
    count,
    meta,
    hint,
    // Injected by DashboardMenu. Not part of the public API.
    tabIndex = -1,
    onKeyDown,
    closeMenu,
    ...rest
  } = props;

  const classes = classNames(rootClassName || css.root, className, {
    [css.selected]: selected,
    [css.destructive]: destructive,
    [css.disabled]: disabled,
  });

  const label = hint ? (
    <span className={css.stack}>
      <span className={css.labelText}>{children}</span>
      <span className={css.hint}>{hint}</span>
    </span>
  ) : (
    <span className={css.label}>{children}</span>
  );

  const trailing = (
    <>
      {meta ? <span className={css.meta}>{meta}</span> : null}
      {count != null ? <span className={css.count}>{count}</span> : null}
      {selected ? <CheckGlyph /> : null}
    </>
  );

  const shared = {
    ref,
    className: classes,
    role: 'menuitem',
    tabIndex: disabled ? -1 : tabIndex,
    onKeyDown,
    ...(disabled ? { 'aria-disabled': true } : {}),
    ...(selected ? { 'aria-current': 'page' } : {}),
    ...rest,
  };

  if (to && !disabled) {
    const linkProps = typeof to === 'string' ? { name: to } : to;
    return (
      <NamedLink
        {...shared}
        {...linkProps}
        onClick={e => {
          if (onClick) {
            onClick(e);
          }
          if (closeMenu) {
            closeMenu(false);
          }
        }}
      >
        {label}
        {trailing}
      </NamedLink>
    );
  }

  return (
    <button
      {...shared}
      type="button"
      onClick={e => {
        // aria-disabled keeps the row announced, so activation is blocked here
        // rather than with the `disabled` attribute, which removes it from the
        // tab order and from most screen reader item counts.
        if (disabled) {
          e.preventDefault();
          return;
        }
        if (onClick) {
          onClick(e);
        }
        if (closeMenu) {
          closeMenu(true);
        }
      }}
    >
      {label}
      {trailing}
    </button>
  );
});

DashboardMenuItem.displayName = 'DashboardMenuItem';

export default DashboardMenuItem;
